import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import "../assets/css/StoreReady.css";

// Set by signup.js after the OTP is verified; the popup is shown only then.
// Holds the store link from the verifyOtp response.
export const STORE_READY_KEY = "store_ready";
// Set by Skip and Buy Plan: the onboarding slider (OnboardingSlider.js), shown
// next on the dashboard (after payment, for Buy Plan), then leads to the
// product list (/gift) instead of the plan page or the add-product page.
export const ONBOARDING_TO_PRODUCTS_KEY = "onboarding_to_products";

const readSignup = () => {
    try {
        return JSON.parse(window.sessionStorage.getItem(STORE_READY_KEY));
    } catch (e) {
        return null;
    }
};

// The store's address from the verifyOtp response. The API sends it only for
// an active web store, on its own store domain; otherwise the popup offers
// to set one up rather than show a link that doesn't work yet.
export const storeUrlFrom = (signup) => signup?.store_url || "";

const copyText = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return;
    }
    // Fallback for http (local) and older browsers
    const el = document.createElement("textarea");
    el.value = text;
    el.setAttribute("readonly", "");
    el.style.position = "absolute";
    el.style.left = "-9999px";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(el);
    if (!ok) throw new Error("copy failed");
};

// "Your store is ready!" popup over the dashboard, once, right after signup.
// Rendered by App.js for logged-in sellers; renders nothing otherwise.
const StoreReady = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [signup, setSignup] = useState(readSignup);
    const [copied, setCopied] = useState(false);
    const dialogRef = useRef(null);

    const open = Boolean(signup);

    useEffect(() => {
        if (!open) return undefined;
        const previous = document.activeElement;
        dialogRef.current?.focus();
        const overflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = overflow;
            // Back to where focus was before the popup opened
            if (previous && typeof previous.focus === "function" && document.contains(previous)) previous.focus();
        };
    }, [open]);

    useEffect(() => {
        if (!copied) return undefined;
        const timer = setTimeout(() => setCopied(false), 1500);
        return () => clearTimeout(timer);
    }, [copied]);

    if (!open) return null;

    const storeUrl = storeUrlFrom(signup);

    const close = () => {
        window.sessionStorage.removeItem(STORE_READY_KEY);
        setSignup(null);
    };

    // Also what "+ Add Products" does: back to the product list behind the popup
    const handleSkip = () => {
        close();
        window.sessionStorage.setItem(ONBOARDING_TO_PRODUCTS_KEY, "true");
    };

    const handleBuyPlan = () => {
        close();
        // plans.js reloads /gift after a successful payment
        window.sessionStorage.setItem(ONBOARDING_TO_PRODUCTS_KEY, "true");
        navigate("/purchase-plan");
    };

    const handleCopy = async () => {
        try {
            await copyText(storeUrl);
            setCopied(true);
        } catch (e) {
            toast.error(t("store_ready.copy_failed"));
        }
    };

    // Escape works as Skip; Tab and Shift+Tab stay inside the popup
    const handleKeyDown = (e) => {
        if (e.key === "Escape") {
            e.preventDefault();
            handleSkip();
            return;
        }
        if (e.key !== "Tab") return;
        const focusable = dialogRef.current.querySelectorAll("a[href], button:not([disabled])");
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    };

    return (
        <div className="sr-overlay">
            <div
                className="sr-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="sr-title"
                tabIndex={-1}
                ref={dialogRef}
                onKeyDown={handleKeyDown}
            >
                <div className="sr-right">
                    <h1 id="sr-title" className="sr-title">{t("store_ready.title")}</h1>

                    <div className="sr-url">
                        <div>
                            <small>{t("store_ready.url_label")}</small>
                            {storeUrl ? (
                                <a href={storeUrl} target="_blank" rel="noopener noreferrer">
                                    {storeUrl.replace(/^https?:\/\//, "")}
                                </a>
                            ) : (
                                <button
                                    type="button"
                                    className="sr-link-btn"
                                    onClick={() => { close(); navigate("/webstore_settings"); }}
                                >
                                    {t("store_ready.set_up_url")}
                                </button>
                            )}
                        </div>
                        {storeUrl && (
                            <button type="button" className="sr-cp" onClick={handleCopy}>
                                {copied ? t("store_ready.copied") : t("store_ready.copy")}
                            </button>
                        )}
                    </div>

                    <div className="sr-gift">
                        <div className="sr-ic" aria-hidden="true">📦</div>
                        <div>
                            <h3>{t("store_ready.products_title")}</h3>
                            <p>{t("store_ready.products_text")}</p>
                            <button type="button" className="sr-add" onClick={handleSkip}>
                                {t("store_ready.add_products")}
                            </button>
                        </div>
                    </div>

                    <div className="sr-foot">
                        <div className="sr-btns">
                            <button type="button" className="sr-b" onClick={handleSkip}>
                                {t("store_ready.skip")}
                            </button>
                            <button type="button" className="sr-b sr-pri" onClick={handleBuyPlan}>
                                {t("store_ready.buy_plan")}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StoreReady;
